<template>
    <div class="page-content">
        <div class="container">
            <div class="heading">
                <h1>{{ page.title }}</h1>
                <p>
                    {{ page.subtitle }}
                </p>
            </div>
        </div>

        <div class="full-width">
            <div class="container">
                <no-ssr placeholder="Loading...">
                    <agile
                        :dots="true"
                        :infinite="true"
                        :autoplay="false"
                        :fade="true"
                        :nav-buttons="true"
                        :center-mode="true"
                        :rtl="false"
                        :slidestoshow="1"
                    >
                        <div
                            v-for="chunk in chunked"
                            :key="chunk.id"
                            class="slide"
                        >
                            <div v-for="post in chunk" :key="post.slug">
                                <nuxt-link :to="`/blog/${post.slug}`">
                                    <el-card shadow="hover">
                                        <div
                                            :style="{
                                                backgroundImage:
                                                    'url(' + post.image + ')'
                                            }"
                                            :alt="post.title"
                                            class="post-img lazyload"
                                        />
                                        <h4>{{ post.title }}</h4>
                                        <div>
                                            <p v-html="post.extract" />
                                        </div>
                                    </el-card>
                                </nuxt-link>
                            </div>
                        </div>
                    </agile>
                </no-ssr>
            </div>
        </div>

        <div class="living-creatures container marginT">
            <el-row :gutter="40">
                <el-col :xs="24" :sm="24" :md="12">
                    <img
                        data-src="~/assets/images/big-tree.jpg"
                        alt="Metropolitan Arborist"
                        class="banner-img mobile lazyload"
                    />
                    <span class="credits">
                        Photo by
                        <a
                            href="https://unsplash.com/@jeremybishop?utm_source=unsplash&utm_medium=referral&utm_content=creditCopyText"
                        >
                            Jeremy Bishop
                        </a>
                        on
                        <a
                            href="https://unsplash.com/s/photos/trees?utm_source=unsplash&utm_medium=referral&utm_content=creditCopyText"
                        >
                            Unsplash
                        </a>
                    </span>
                    <div class="column-layer hide-tablet" />
                </el-col>
                <el-col :xs="24" :sm="24" :md="12">
                    <div v-for="node in accordions" :key="node.id">
                        <div v-if="node.slug === 'living-creatures'">
                            <h3>{{ node.node_structure.name }}</h3>
                            <span class="separator"></span>
                            <ol>
                                <li
                                    v-for="(item, index) in node.node_structure
                                        .items"
                                    :key="index"
                                >
                                    <h4>{{ item.title }}</h4>
                                    <div
                                        class="paragraph"
                                        v-html="item.content"
                                    />
                                </li>
                            </ol>
                        </div>
                    </div>
                </el-col>
            </el-row>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex'
import chunk from 'lodash/chunk'

export default {
    async asyncData({ app, store, error }) {
        await store
            .dispatch('general/getPage', app.context.route.name)
            .catch((e) => {
                error({
                    statusCode: e.response.status,
                    message: 'Page not found'
                })
            })
    },

    head() {
        return {
            title: this.page.title,
            meta: [
                {
                    hid: 'title',
                    name: 'title',
                    content: this.page.meta_title
                },
                {
                    hid: 'description',
                    name: 'description',
                    content: this.page.meta_description
                }
            ]
        }
    },

    computed: {
        ...mapGetters({
            page: 'general/page',
            page_contents: 'general/page_contents'
        }),

        accordions() {
            return this.page.nodes.filter(function(el) {
                return el.node_structure.type_section === 'accordion'
            })
        },

        chunked() {
            return this.page_contents.posts
                ? chunk(this.page_contents.posts.data, 6)
                : ''
        }
    }
}
</script>

<style lang="scss">
.agile {
    &__nav-button {
        background: transparent;
        border: none;
        color: #000000;
        cursor: pointer;
        font-size: 24px;
        height: 100%;
        position: absolute;
        top: 0;
        transition-duration: 0.3s;
        width: 4rem;

        &:hover {
            background-color: rgba(#000, 0.5);
            opacity: 1;
        }

        &--prev {
            left: -4rem;
            line-height: 1rem;
            font-size: 42px;
            font-family: cursive;
        }

        &--next {
            right: -4rem;
            line-height: 1rem;
            font-size: 42px;
            font-family: cursive;
        }
    }

    &__dots {
        left: 50%;
        position: absolute;
        transform: translateX(-50%);
    }

    &__dot {
        margin: 0 10px;

        button {
            background-color: transparent;
            border: 1px solid grey;
            border-radius: 50%;
            cursor: pointer;
            display: block;
            height: 15px;
            font-size: 0;
            line-height: 0;
            margin: 0;
            padding: 0;
            transition-duration: 0.3s;
            width: 15px;
        }

        &--current,
        &:hover {
            button {
                background-color: #fff;
            }
        }
    }
}

@media only screen and (max-width: 991px) {
    .agile {
        margin-top: 20px;

        &__nav-button {
            display: none;
        }

        &__dots {
            margin-bottom: 20px;
        }

        &__dot {
            button {
                height: 40px;
                width: 40px;
            }
        }
    }
}

@media only screen and (max-width: 767px) {
    .agile {
        margin-top: 20px;

        &__nav-button {
            display: none;
        }

        &__dots {
            margin-bottom: 20px;
        }

        &__dot {
            button {
                height: 40px;
                width: 40px;
            }
        }
    }
}
</style>

<style lang="scss" scoped>
.full-width {
    display: inline-block;
    width: 100%;
    background: #e7f7ef;
    padding: 1em 0 3em 0;
}

.agile {
    .slide {
        grid-template-columns: repeat(3, 1fr);
        grid-gap: 20px;
        display: grid;

        h4 {
            margin-bottom: 0.5em;
        }

        p {
            margin: 0;
            font-size: 90%;
            color: $body-light;
        }

        .el-col {
            margin-top: 2em;
        }
    }
}

.post-img {
    display: block;
    width: 100%;
    padding-bottom: 100%;
    background-size: cover;
    background-position: center center;
    border-radius: 5px;
}

.el-card {
    height: 100%;
}

.living-creatures {
    img {
        width: 100%;
        padding: 60px;
    }
    h3 {
        margin-top: 6%;
    }
    .credits {
        font-size: 0.5rem;
        float: right;
        padding-right: 60px;
        margin-top: -40px;
    }
    ol {
        li {
            .paragraph {
                color: #747d7a;
                font-size: 0.85rem;
            }
        }
    }
    .column-layer {
        background: #f6f7f9;
        position: absolute;
        bottom: 210px;
        height: 73%;
        width: 32%;
        z-index: -1;
        right: auto;
    }
}

@media only screen and (max-width: 991px) {
    .full-width {
        padding: 0em 0 2em 0;
    }
    .column-layer {
        display: none;
    }
}

@media only screen and (max-width: 767px) {
    .agile {
        .slide {
            grid-template-columns: 1fr;

            .el-card {
                margin: 30px;
            }
        }
    }
    .living-creatures {
        h2 {
            text-align: center;
        }
        .separator {
            margin: 0 auto;
        }
    }
    .column-layer {
        display: none;
    }
}
</style>
